import { Controller } from "@hotwired/stimulus"

const FRAME_MS = 300
const HOLD_MS = 5000
const WINDOW_FRAMES = 24 // fixed 2h loop over 5-min frames — the legacy GIF window
const BURGENLAND_CENTER_X = 0.7 // Burgenland's center as a fraction of the frame width

export class RadarController extends Controller {
    static targets = ["tick", "image", "slider", "timestamp", "playButton", "liveBadge", "stage"]

    connect() {
        this.frames = []
        this.index = -1
        this.playing = false
        this.preloaded = new Set()
        this.readFrames(this.tickTarget)
        if (this.frames.length && this.hasImageTarget) {
            this.preloadWindow()
            this.show(this.windowStart())
            this.playing = true
            this.scheduleNext()
        }
        this.syncControls()
        this.connected = true
    }

    disconnect() {
        this.stopTimer()
    }

    // Turbo replaces the tick on ingest; re-anchor to the shown frame by timestamp.
    tickTargetConnected(element) {
        if (!this.connected) return // initial render — connect() already consumed the inline state
        const shown = this.frames[this.index]?.t
        this.readFrames(element)
        if (!this.frames.length || !this.hasImageTarget) { this.pause(); return }
        this.preloadWindow()
        const index = this.frames.findIndex((frame) => frame.t === shown)
        this.show(index === -1 ? (this.playing ? this.windowStart() : 0) : index)
        if (this.playing) this.scheduleNext()
    }

    readFrames(element) {
        this.frames = JSON.parse(element.dataset.frames)
    }

    windowStart() {
        return Math.max(this.frames.length - WINDOW_FRAMES, 0)
    }

    preloadWindow() {
        this.frames.slice(this.windowStart()).forEach(({ url }) => {
            if (this.preloaded.has(url)) return
            this.preloaded.add(url)
            new Image().src = url
        })
    }

    show(index) {
        const frame = this.frames[index]
        if (!frame || !this.hasImageTarget) return // empty archive or empty-state page — controls are no-ops
        this.index = index
        this.imageTarget.src = frame.url
        this.timestampTarget.textContent = this.formatTimestamp(frame.t)
        this.syncControls()
    }

    formatTimestamp(epoch) {
        const options = window.matchMedia("(min-width: 1200px)").matches
            ? { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Vienna" }
            : { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Vienna" }
        return new Intl.DateTimeFormat("de-AT", options).format(new Date(epoch * 1000))
    }

    scheduleNext() {
        this.stopTimer()
        this.timer = setTimeout(() => this.advance(), this.index >= this.frames.length - 1 ? HOLD_MS : FRAME_MS)
    }

    advance() {
        if (!this.playing) return
        this.show(this.index >= this.frames.length - 1 ? this.windowStart() : this.index + 1)
        this.scheduleNext()
    }

    togglePlay() {
        if (this.playing) return this.pause()
        if (!this.frames.length || !this.hasImageTarget) return
        this.playing = true
        this.show(this.index < this.windowStart() ? this.windowStart() : this.index)
        this.scheduleNext()
    }

    pause() {
        this.playing = false
        this.stopTimer()
        this.syncControls()
    }

    sliderChanged(event) {
        const index = Number(event.target.value) // read before pause() — its syncControls resets the slider value
        this.pause()
        this.show(index)
    }

    stepBack() {
        this.pause()
        this.show(Math.max(this.index - 1, 0))
    }

    stepForward() {
        this.pause()
        this.show(Math.min(this.index + 1, this.frames.length - 1))
    }

    goLive() {
        if (!this.frames.length || !this.hasImageTarget) return
        this.playing = true
        this.show(this.frames.length - 1)
        this.scheduleNext()
    }

    // A just-pruned frame can 404 until the next tick replaces the list — skip forward, but stop after a full window of errors.
    imageError() {
        if (!this.playing) return
        if ((this.errorStreak = (this.errorStreak || 0) + 1) > WINDOW_FRAMES) return this.pause()
        this.advance()
    }

    imageLoaded() {
        this.errorStreak = 0
        this.scrollToBurgenland()
    }

    // Zoomed native stage (.wm-radar-zoomed): start the horizontal scroller on
    // Burgenland once, as soon as the first frame gives the stage its overflow.
    scrollToBurgenland() {
        if (this.scrolledToBurgenland || !this.hasStageTarget) return
        const stage = this.stageTarget
        const overflow = stage.scrollWidth - stage.clientWidth
        if (overflow <= 0) return
        this.scrolledToBurgenland = true
        stage.scrollLeft = Math.min(Math.max(stage.scrollWidth * BURGENLAND_CENTER_X - stage.clientWidth / 2, 0), overflow)
    }

    stopTimer() {
        clearTimeout(this.timer)
        this.timer = null
    }

    syncControls() {
        const empty = this.frames.length === 0
        this.sliderTarget.max = Math.max(this.frames.length - 1, 0)
        this.sliderTarget.value = this.index
        this.sliderTarget.disabled = empty
        this.playButtonTarget.disabled = empty
        this.playButtonTarget.classList.toggle("wm-playing", this.playing)
        this.playButtonTarget.title = this.playing ? "Pause" : "Wiedergabe"
        this.liveBadgeTarget.classList.toggle("active", this.playing)
        this.liveBadgeTarget.disabled = empty
    }
}
